import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";

/** Slugs premarcados al abrir (mockup Para Revisar). Web = sooprema siempre. */
const WEB_SLUG = "sooprema";
const DEFAULT_SLUGS = ["fotocasa", "pisos", WEB_SLUG];

/**
 * Modal al marcar «Publicaciones» en Para Revisar.
 * Solo guarda la elección (web ± portales). El alta real en portales
 * ocurre cuando Marco termina fotos + fusión NAS.
 */
export default function ModalPortalesAltaWeb({
  isOpen,
  initialSlugs = null,
  onClose,
  onConfirm,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [portales, setPortales] = useState([]);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    let cancel = false;
    setCargando(true);
    setError("");
    setEnviando(false);
    (async () => {
      try {
        const res = await api.get("/portales");
        const rows = (res.data?.data || res.data || []).filter(
          (p) => p && p.activo !== false
        );
        if (cancel) return;
        setPortales(rows);
        if (Array.isArray(initialSlugs)) {
          setSeleccion(
            new Set(
              initialSlugs
                .map((s) => String(s).toLowerCase())
                .filter((s) =>
                  rows.some((p) => String(p.slug || "").toLowerCase() === s)
                )
            )
          );
        } else {
          setSeleccion(
            new Set(
              DEFAULT_SLUGS.filter((s) =>
                rows.some((p) => String(p.slug || "").toLowerCase() === s)
              )
            )
          );
        }
      } catch (e) {
        if (!cancel) {
          setError(e.response?.data?.error || e.message || "No se pudieron cargar los portales");
          setPortales([]);
        }
      } finally {
        if (!cancel) setCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isOpen, initialSlugs]);

  const flatSlugs = useMemo(
    () => portales.map((p) => String(p.slug || "").toLowerCase()).filter(Boolean),
    [portales]
  );

  if (!isOpen) return null;

  const toggleSlug = (slug) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const confirmar = async (soloWeb) => {
    setEnviando(true);
    setError("");
    try {
      // Web (slug técnico sooprema) siempre en slugs si se confirma publicar en web.
      const picked = soloWeb
        ? [WEB_SLUG]
        : [...seleccion].filter((s) => flatSlugs.includes(s));
      if (!picked.includes(WEB_SLUG) && flatSlugs.includes(WEB_SLUG)) {
        picked.push(WEB_SLUG);
      } else if (!picked.includes(WEB_SLUG)) {
        picked.push(WEB_SLUG);
      }
      await onConfirm?.({ soloWeb, slugs: [...new Set(picked)] });
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "No se pudo guardar");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={() => {
        if (!enviando) onClose?.();
      }}
    >
      <div
        style={{
          background: c.bgPanel,
          borderRadius: 12,
          padding: 20,
          width: "100%",
          maxWidth: 380,
          border: `1px solid ${c.border}`,
          boxShadow: isDark
            ? "0 16px 48px rgba(0,0,0,0.5)"
            : "0 16px 48px rgba(0,0,0,0.12)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            margin: "0 0 8px",
            fontSize: 17,
            fontWeight: 700,
            color: c.textPrimary,
          }}
        >
          ¿Publicar también en portales?
        </h3>
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 13,
            lineHeight: 1.45,
            color: c.textSecondary,
          }}
        >
          La propiedad se publicará en la web de Vitalcasa. Elige si quieres
          añadirla también a estos portales (se subirán cuando Marco termine de
          editar fotos y multimedia):
        </p>

        {cargando ? (
          <div style={{ fontSize: 13, color: c.textMuted, padding: "8px 0" }}>
            Cargando portales…
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {portales.map((p) => {
              const slug = String(p.slug || "").toLowerCase();
              const checked = seleccion.has(slug);
              return (
                <label
                  key={p.id || slug}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${
                      checked ? c.chipActiveBorder || c.border : c.border
                    }`,
                    background: checked
                      ? c.chipActiveBg || (isDark ? "#1a2332" : "#f0f7ff")
                      : c.bgCard || "transparent",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSlug(slug)}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: p.color || c.textMuted,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: c.textPrimary,
                    }}
                  >
                    {p.nombre || slug}
                  </span>
                </label>
              );
            })}
            {!portales.length && !error && (
              <div style={{ fontSize: 12, color: c.textMuted }}>
                No hay portales activos en la matriz.
              </div>
            )}
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              color: c.accentRed || "#dc2626",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 18,
          }}
        >
          <button
            type="button"
            disabled={enviando}
            onClick={() => confirmar(true)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              background: "transparent",
              color: c.textPrimary,
              fontSize: 13,
              fontWeight: 600,
              cursor: enviando ? "wait" : "pointer",
            }}
          >
            Solo web
          </button>
          <button
            type="button"
            disabled={enviando || cargando}
            onClick={() => confirmar(false)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: c.accent || "#2563eb",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: enviando || cargando ? "wait" : "pointer",
            }}
          >
            Publicar
          </button>
        </div>
      </div>
    </div>
  );
}
